import {Box, Divider, Grid, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import AutoField from '../../../components/AutoForm/AutoFieldNew';
import {useAutoField, useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {useContributionPotsQuery} from './useContributionPotsQuery';
import useComboFrequency from '../../../hooks/useComboFrequency';
import {getISODateOrEmptyString} from '../../../utils/getISODate';
import {newUTCDate} from '../../../utils/newUTCDate';
import {CASH_OBJ} from '../../../utils/potsFilter';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {InitialBuilderFeeField} from '../CreateBuilder/BuilderInitialFeesField';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {
  ContributeAmountRecurrentField,
  ContributeAmountSingleField,
  contributeFieldType,
  contributeFromType,
  ContributeStartField,
  contributeFrequencyComboField as frequencyComboField,
  contributeFullField as fullField,
  contributeInSpecieSourceField as inSpecieSourceField,
  contributeAmountField as builderAmountField,
} from './builderContributeFields';
import {
  useCalculateNetRecurringClient,
  useCalculateNetSingleClient,
  // useDeriveIsLisaContribute,
  useSetContributeFullAmount,
  setMinimumDateMustBe3WeeksFromToday,
} from '../hooks/builderContributeHooks';
import {
  contributionIsActuallyTransfer,
  shouldShowEmployerContribution,
} from './BuilderContributeHelpers';
import {useIsMinor} from '../hooks/createBuilderHooks';

interface ContributePot {
  isCash?: boolean | null;
  totalValue?: string | null;
}

export interface ExistingContributePlanProps {
  pot?: ContributePot | null;
  maxAmount?: number;
}

export const BuilderContributeSourceFields = ({
  pot,
  maxAmount,
}: ExistingContributePlanProps) => {
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  const isMinor = useIsMinor();
  const [contributeFieldObj, contributeMeta, contributeHelpers] =
    useAutoField<builderFieldsType['contribute']>('contribute');

  const [contributeFromFieldObj, contributeFromMeta, contributeFromHelpers] =
    useAutoField<contributeFieldType['contributeFrom']>('contribute.contributeFrom');
  const [
    contributeFromClientFieldObj,
    contributeFromClientMeta,
    contributeFromClientHelpers,
  ] = useAutoField<contributeFromType['singleClientAmount']>(
    'contribute.contributeFrom.singleClientAmount'
  );
  const [contributeFromNetFieldObj, contributeFromNetMeta, contributeFromNetHelpers] =
    useAutoField<contributeFromType['netSingleClientAmount']>(
      'contribute.contributeFrom.netSingleClientAmount'
    );
  const [
    contributeFromNetRecurringFieldObj,
    contributeFromNetRecurringMeta,
    contributeFromNetRecurringHelpers,
  ] = useAutoField<contributeFromType['netSingleClientAmount']>(
    'contribute.contributeFrom.netRecurringClientAmount'
  );
  const contributeValues = contributeFieldObj.value;
  const {sourcePots, destinationPots} = useContributionPotsQuery();
  const pots = [...sourcePots, ...destinationPots];
  const {builderDetails} = useBuilderContext();
  const {hasSingle, hasRecurring} = useComboFrequency(values.contribute?.comboFrequency);
  const [previousDate, setPreviousDate] = useState(
    getISODateOrEmptyString(builderDetails?.startDate)?.split('T')?.[0]
  );
  // contributions ONLY expect cash contribution which still have source of funds
  const isCash = values.contribute?.contributeFrom?.id === CASH_OBJ.id || pot?.isCash;

  useEffect(() => {
    setMinimumDateMustBe3WeeksFromToday({
      contributeValues,
      setFieldValue,
      setPreviousDate,
      previousDate,
      today: newUTCDate(),
    });
  }, [
    contributeValues?.contributeFrom?.start,
    contributeValues?.comboFrequency,
    previousDate,
  ]);

  useSetContributeFullAmount({
    contributeFromFieldObj,
    maxAmount,
    contributeFromClientHelpers,
  });

  useCalculateNetSingleClient({
    contributeFromFieldObj,
    isCash,
    contributeFromNetHelpers,
  });

  useCalculateNetRecurringClient({
    contributeFromFieldObj,
    isCash,
    contributeFromNetRecurringHelpers,
  });

  // const isLisaDestination = useDeriveIsLisaContribute({
  //   contributeFromHelpers,
  //   contributeFromFieldObj,
  // });

  const normalSourcePlanGoingToPension = values.mainPlanType === 'pension' && !isCash;

  const specialFields = (
    <>
      <Grid item xs={6}>
        <AutoField
          field={{
            ...inSpecieSourceField,
            id: `contribute.contributeFrom.inSpecie`,
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <AutoField
          field={{
            ...fullField,
            disabled: !pot?.totalValue,
            label: pot?.totalValue
              ? fullField.label
              : `${fullField.label} (requires > £0.00)`,
            id: `contribute.contributeFrom.full`,
          }}
        />
      </Grid>
    </>
  );

  if (values.mainPlanType === 'pension') {
    return (
      <Box>
        <Box mt={2} />
        <Grid container direction="row" spacing={3}>
          <Grid item xs={6}>
            <AutoField field={frequencyComboField} />
          </Grid>
          <Grid item xs={6}>
            {hasRecurring && (
              <AutoField
                field={ContributeStartField}
                key={`${contributeValues?.contributeFrom?.start}`}
                helperText="Minimum start date: 3 weeks from now"
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Box mt={2} />
        <Typography variant="h6">Personal contribution:</Typography>
        <Box mt={2} />
        <Grid container direction="row" spacing={3}>
          {hasSingle && (
            <Grid item xs={6}>
              <AutoField
                field={{
                  ...ContributeAmountSingleField,
                  allowZero: true,
                  canBeEmpty: true,
                  label: 'Personal Single Amount (Gross)',
                }}
              />
            </Grid>
          )}
          {hasSingle && normalSourcePlanGoingToPension && (
            <Grid item xs={6}>
              <AutoField
                field={{
                  ...ContributeAmountSingleField,
                  id: 'contribute.contributeFrom.netSingleClientAmount',
                  disabled: true,
                  allowZero: true,
                  canBeEmpty: true,
                  label: 'Personal Single Amount (Net)',
                }}
              />
            </Grid>
          )}
          {hasSingle ? (
            <Grid item xs={6}>
              <InitialBuilderFeeField formikKeyPrefixWithoutFee="contribute.contributeFrom" />
            </Grid>
          ) : null}
          {hasRecurring && (
            <Grid item xs={6}>
              <AutoField
                field={{
                  ...ContributeAmountRecurrentField,
                  allowZero: true,
                  canBeEmpty: true,
                  label: 'Personal Recurring Amount (Gross)',
                }}
              />
            </Grid>
          )}
          {hasRecurring && normalSourcePlanGoingToPension && (
            <Grid item xs={6}>
              <AutoField
                field={{
                  ...ContributeAmountRecurrentField,
                  id: 'contribute.contributeFrom.netRecurringClientAmount',
                  allowZero: true,
                  canBeEmpty: true,
                  disabled: true,
                  label: 'Personal Recurring Amount (Net)',
                }}
              />
            </Grid>
          )}
        </Grid>
        {shouldShowEmployerContribution({
          isMinor,
          mainPlanType: values.mainPlanType,
        }) && (
          <>
            <Box mt={2} />
            <Divider />
            <Box mt={3} />
            <Typography variant="h6">Employer contribution:</Typography>
            <Box mt={2} />
            <Grid container direction="row" spacing={3}>
              {hasSingle && (
                <Grid item xs={6}>
                  <AutoField
                    field={{
                      ...builderAmountField,
                      allowZero: true,
                      canBeEmpty: true,
                      label: 'Employer Single Amount (Gross)',
                      id: 'contribute.contributeFrom.singleEmployerAmount',
                    }}
                  />
                </Grid>
              )}
              {hasSingle && (
                <Grid item xs={6}>
                  <InitialBuilderFeeField
                    employers
                    formikKeyPrefixWithoutFee="contribute.contributeFrom"
                  />
                </Grid>
              )}
              {hasRecurring && (
                <Grid item xs={6}>
                  <AutoField
                    field={{
                      ...builderAmountField,
                      allowZero: true,
                      canBeEmpty: true,
                      label: 'Employer Recurring Amount (Gross)',
                      id: 'contribute.contributeFrom.recurringEmployerAmount',
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Grid container direction="row" spacing={3}>
        <Grid item xs={12} style={{marginTop: '1rem'}}>
          <AutoField field={frequencyComboField} />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {hasSingle && (
          <>
            <Grid item xs={6}>
              <AutoField
                field={{
                  ...ContributeAmountSingleField,
                  allowZero: true,
                  canBeEmpty: true,
                  label: 'Personal Single Amount',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InitialBuilderFeeField
                formikKeyPrefixWithoutFee="contribute.contributeFrom"
                // disabled={isLisaDestination} // No initial on contribution into LISA
              />
            </Grid>
            {contributionIsActuallyTransfer({values, pots}) ? specialFields : null}
          </>
        )}
        {hasSingle && hasRecurring && (
          <Grid item xs={12}>
            <Divider />
          </Grid>
        )}
        {hasRecurring && (
          <>
            <Grid item xs={6}>
              <AutoField
                field={{
                  ...ContributeAmountRecurrentField,
                  allowZero: true,
                  canBeEmpty: true,
                  label: 'Personal Recurring Amount',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              {hasRecurring && (
                <AutoField
                  field={ContributeStartField}
                  key={`${contributeValues?.contributeFrom?.id}`}
                  helperText="Minimum start date: 3 weeks from now"
                />
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};
